import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User, Grid, CreditCard, Gift, Award, Plus, LogOut, Heart, Package, Bell } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Header } from '@/components/Header';

interface AccountLayoutProps {
  children: ReactNode;
}

export const AccountLayout = ({ children }: AccountLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isProMember, isAdmin, signOut } = useAuth();
  const { membership } = useAuth();
  const isFounder = membership?.tier === 'founder';
  
  // Check if user is a seller (has editor or admin role for now)
  const isSeller = isAdmin;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const menuItems = [
    { label: 'Profile', icon: User, path: '/account/profile', show: true },
    { label: 'Favorites', icon: Heart, path: '/account/favorites', show: true },
    { label: 'My Tools', icon: Package, path: '/account/tech-stack', show: true },
    { label: 'Price Alerts', icon: Bell, path: '/account/price-alerts', show: true },
    { label: 'Products', icon: Grid, path: '/account/products', show: isSeller },
    { label: 'Payments & billing', icon: CreditCard, path: '/account/payments', show: true },
    { label: 'Credits & gift cards', icon: Gift, path: '/account/credits', show: true },
    { label: 'Rewards', icon: Award, path: '/account/rewards', show: true },
    { label: 'Pro membership', icon: Plus, path: '/account/pro', show: !isFounder && !isProMember },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Navigation */}
          <aside className="w-full md:w-60 shrink-0">
            <nav className="space-y-1 bg-card rounded-lg border p-2">
              {menuItems.filter(item => item.show).map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-md transition-colors",
                      isActive 
                        ? "bg-emerald-50 text-emerald-900" 
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
              
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Log out
              </button>
            </nav>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};
